import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Table,
  Group,
  Select,
  Pagination,
  Loader,
  Badge,
  Paper,
  TextInput,
  Button,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import {
  IconSearch,
  IconDownload,
  IconCopy,
  IconCheck,
} from "@tabler/icons-react";
import { useAuditLogs } from "../../hooks/admin/useAuditLogs";
import { useUsers } from "../../hooks/useAdmin";
import {
  AuditLogFilters,
  auditLogService,
} from "../../services/admin/auditLog.service";
import {
  getLabTodayDateOnly,
  parseDateOnly,
  type DateOnlyString,
} from "../../utils/dateOnly";
import { LAB_TIME_ZONE } from "../../utils/labTime";

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "view", label: "View" },
  { value: "create", label: "Create" },
  { value: "update", label: "Update" },
  { value: "delete", label: "Delete" },
  { value: "login", label: "Login" },
  { value: "logout", label: "Logout" },
  { value: "export", label: "Export" },
  { value: "print", label: "Print" },
  { value: "page_visit", label: "Page Visit" },
];

const RESOURCE_TYPE_OPTIONS = [
  { value: "", label: "All Resources" },
  { value: "case", label: "Case" },
  { value: "patient", label: "Patient" },
  { value: "report", label: "Report" },
  { value: "document", label: "Document" },
  { value: "session", label: "Session" },
  { value: "case_images", label: "Case Images" },
  { value: "send_out", label: "Send Out" },
  { value: "page", label: "Page" },
  { value: "specimen", label: "Specimen" },
  { value: "specimen_list", label: "Specimen List" },
  { value: "grossing", label: "Grossing" },
  { value: "diagnosis", label: "Diagnosis" },
];

const ACTION_COLORS: Record<string, string> = {
  view: "blue",
  create: "green",
  update: "yellow",
  delete: "red",
  login: "teal",
  logout: "gray",
  export: "violet",
  print: "indigo",
  page_visit: "cyan",
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-US", {
    timeZone: LAB_TIME_ZONE,
  });
}

type AuditDateRange = [DateOnlyString | null, DateOnlyString | null];

export default function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState<string>("");
  const [resourceType, setResourceType] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [dateRange, setDateRange] = useState<AuditDateRange>([null, null]);
  const [resourceId, setResourceId] = useState<string>("");
  const maximumDate = useMemo(() => getLabTodayDateOnly(), []);

  const { data: users = [] } = useUsers();

  const userOptions = useMemo(
    () => [
      { value: "", label: "All Users" },
      ...users.map((u: any) => ({
        value: u.id,
        label: u.fullName || u.username,
      })),
    ],
    [users]
  );

  const filters = useMemo<AuditLogFilters>(
    () => ({
      page,
      limit: 50,
      ...(action && { action }),
      ...(resourceType && { resourceType }),
      ...(userId && { userId }),
      ...(resourceId && { resourceId }),
      ...(dateRange[0] && { startDate: dateRange[0] }),
      ...(dateRange[1] && { endDate: dateRange[1] }),
    }),
    [action, dateRange, page, resourceId, resourceType, userId]
  );

  const { data: result, isLoading } = useAuditLogs(filters);
  const logs = useMemo(() => result?.data ?? [], [result]);
  const pagination = useMemo(() => result?.pagination, [result]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(action || resourceType || userId || resourceId || dateRange[0]),
    [action, dateRange, resourceId, resourceType, userId]
  );

  const handleUserChange = useCallback((value: string | null) => {
    setUserId(value || "");
    setPage(1);
  }, []);

  const handleActionChange = useCallback((value: string | null) => {
    setAction(value || "");
    setPage(1);
  }, []);

  const handleResourceTypeChange = useCallback((value: string | null) => {
    setResourceType(value || "");
    setPage(1);
  }, []);

  const handleResourceIdChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setResourceId(event.currentTarget.value);
      setPage(1);
    },
    []
  );

  const handleDateRangeChange = useCallback((value: DatesRangeValue) => {
    const startDate = parseDateOnly(value[0]);
    const endDate = parseDateOnly(value[1]);
    const hasInvalidValue =
      (value[0] !== null && !startDate) || (value[1] !== null && !endDate);

    setDateRange(
      hasInvalidValue ? [null, null] : [startDate ?? null, endDate ?? null]
    );
    setPage(1);
  }, []);

  const handleExport = useCallback(() => {
    window.open(auditLogService.exportUrl(filters));
  }, [filters]);

  const handlePageChange = useCallback((value: number) => {
    setPage(value);
  }, []);

  const clearFilters = useCallback(() => {
    setAction("");
    setResourceType("");
    setUserId("");
    setResourceId("");
    setDateRange([null, null]);
    setPage(1);
  }, []);

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="xl">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Audit Logs</Title>
              <Text size="sm" c="dimmed" mt="xs">
                HIPAA-compliant audit trail of all PHI access and modifications
              </Text>
            </div>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </Group>

          <Paper p="md" withBorder>
            <Group grow align="end">
              <Select
                label="User"
                placeholder="All Users"
                data={userOptions}
                value={userId}
                onChange={handleUserChange}
                searchable
                clearable
                leftSection={<IconSearch size={14} />}
              />
              <Select
                label="Action"
                placeholder="All Actions"
                data={ACTION_OPTIONS}
                value={action}
                onChange={handleActionChange}
                clearable
              />
              <Select
                label="Resource Type"
                placeholder="All Resources"
                data={RESOURCE_TYPE_OPTIONS}
                value={resourceType}
                onChange={handleResourceTypeChange}
                clearable
              />
              <TextInput
                label="Resource ID"
                placeholder="Filter by UUID"
                value={resourceId}
                onChange={handleResourceIdChange}
              />
              <DatePickerInput
                type="range"
                label="Date Range"
                placeholder="Select date range"
                value={dateRange}
                onChange={handleDateRangeChange}
                maxDate={maximumDate}
                clearable
              />
            </Group>

            {hasActiveFilters && (
              <Group justify="space-between" mt="xs">
                <Group gap="xs" wrap="wrap">
                  {action && (
                    <Badge variant="light" color="blue" size="sm">
                      {action}
                    </Badge>
                  )}
                  {resourceType && (
                    <Badge variant="light" color="grape" size="sm">
                      {resourceType}
                    </Badge>
                  )}
                  {userId && (
                    <Badge variant="light" color="teal" size="sm">
                      User
                    </Badge>
                  )}
                  {resourceId && (
                    <Badge variant="light" color="orange" size="sm">
                      Resource ID
                    </Badge>
                  )}
                  {dateRange[0] && (
                    <Badge variant="light" color="cyan" size="sm">
                      Date range
                    </Badge>
                  )}
                </Group>
                <Button
                  variant="subtle"
                  size="compact-xs"
                  color="gray"
                  onClick={clearFilters}
                >
                  Clear all
                </Button>
              </Group>
            )}
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {pagination
                    ? `${pagination.total} audit log entries`
                    : "Loading…"}
                </Text>
                {pagination && pagination.totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {logs.length} of {pagination.total}
                    </Text>
                    <Pagination
                      total={pagination.totalPages}
                      value={page}
                      onChange={handlePageChange}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Group justify="center" py="xl">
                  <Loader />
                </Group>
              ) : (
                <Table
                  striped
                  highlightOnHover
                  withTableBorder
                  withColumnBorders
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Timestamp</Table.Th>
                      <Table.Th>User</Table.Th>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Resource Type</Table.Th>
                      <Table.Th>Resource ID</Table.Th>
                      <Table.Th>URL</Table.Th>
                      <Table.Th>IP Address</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {logs.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={7}>
                          <Text ta="center" c="dimmed" py="xl">
                            No audit logs found
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      logs.map((log) => (
                        <Table.Tr key={log.id}>
                          <Table.Td>
                            <Text size="sm">{formatDate(log.createdAt)}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {log.user?.fullName ||
                                log.user?.username ||
                                log.userId}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={ACTION_COLORS[log.action] || "gray"}
                              variant="light"
                              size="sm"
                            >
                              {log.action}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" tt="capitalize">
                              {log.resourceType.replace(/_/g, " ")}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {log.resourceId ? (
                              <Group gap={4} wrap="nowrap">
                                <Text size="xs" ff="monospace" c="dimmed">
                                  {log.resourceId.substring(0, 8)}…
                                </Text>
                                <CopyButton
                                  value={log.resourceId}
                                  timeout={2000}
                                >
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied ? "Copied!" : "Copy full ID"
                                      }
                                      withArrow
                                    >
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color={copied ? "teal" : "gray"}
                                        onClick={copy}
                                      >
                                        {copied ? (
                                          <IconCheck size={12} />
                                        ) : (
                                          <IconCopy size={12} />
                                        )}
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Group>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {log.url ? (
                              <Group gap={4} wrap="nowrap">
                                <Text
                                  size="xs"
                                  ff="monospace"
                                  title={log.url}
                                  style={{ flex: 1, minWidth: 0 }}
                                  truncate
                                >
                                  {log.url.length > 50
                                    ? `${log.url.substring(0, 50)}…`
                                    : log.url}
                                </Text>
                                <CopyButton value={log.url} timeout={2000}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={copied ? "Copied!" : "Copy URL"}
                                      withArrow
                                    >
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color={copied ? "teal" : "gray"}
                                        onClick={copy}
                                      >
                                        {copied ? (
                                          <IconCheck size={12} />
                                        ) : (
                                          <IconCopy size={12} />
                                        )}
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Group>
                            ) : (
                              <Text size="xs" c="dimmed">
                                —
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" ff="monospace">
                              {log.ipAddress}
                            </Text>
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </Container>
  );
}
